// CONST

const LOAD_EVENT = 'load';
const CLICK_EVENT = 'click';
const SCROLL_EVENT = 'scroll';
const KEY_EVENT = 'keydown';

const CARROUSEL_PLAYING_CLASSNAME = 'carrousel-card--playing';
const CARROUSEL_PAUSED_CLASSNAME = 'carrousel-card--paused';

const JS_CARROUSEL_SELECTOR = '.js-carrousel';
const JS_CARROUSEL_SLIDES_SELECTOR = '.js-carrousel-slides';
const JS_CARROUSEL_SLIDE_SELECTOR = '.js-carrousel-slide';
const JS_CARROUSEL_TABS_SELECTOR = '.js-carrousel-tabs';
const JS_CARROUSEL_TAB_SELECTOR = '.js-carrousel-tab';
const JS_CARROUSEL_TRANSPORT_SELECTOR = '.js-carrousel-transport';
const JS_CARROUSEL_PREVIOUS_SELECTOR = '.js-carrousel-previous';
const JS_CARROUSEL_NEXT_SELECTOR = '.js-carrousel-next';
const FOCUS_WITHIN_SELECTOR = ':focus-within';
const LINK_SELECTOR = '[data-tabbable]';

const ARROW_LEFT_EVENT_KEY = 'ArrowLeft';
const ARROW_RIGHT_EVENT_KEY = 'ArrowRight';
const HOME_EVENT_KEY = 'Home';
const END_EVENT_KEY = 'End';

const ARIA_SELECTED_ATTR = 'aria-selected';
const TABINDEX_ATTR = 'tabindex';

const TRUE_VALUE = 'true';
const FALSE_VALUE = 'false';
const DISABLED_TABINDEX_VALUE = '-1';
const ENABLED_TABINDEX_VALUE = '0';
const SCROLL_BEHAVIOR_AUTO_VALUE = 'auto';
const SCROLL_BEHAVIOR_SMOOTH_VALUE = 'smooth';

const CARROUSEL_SLIDE_DELAY = 5000;
const DEBOUNCE_DELAY = 150;

// Utils

/**
 * Retourne une fonction qui, tant qu'elle continue à être invoquée,
 * ne sera pas exécutée. La fonction ne sera exécutée que lorsque
 * l'on cessera de l'appeler pendant plus de N millisecondes.
 * Si le paramètre `immediate` vaut vrai, alors la fonction
 * sera exécutée au premier appel au lieu du dernier.
 * Paramètres :
 *  - func : la fonction à `debouncer`
 *  - wait : le nombre de millisecondes (N) à attendre avant
 *           d'appeler func()
 *  - immediate (optionnel) : Appeler func() à la première invocation
 *                            au lieu de la dernière (Faux par défaut)
 *  - context (optionnel) : le contexte dans lequel appeler func()
 *                          (this par défaut)
 */
function debounce(func, wait, immediate, context) {
    var result;
    var timeout = null;
    return function() {
        var ctx = context || this, args = arguments;
        var later = function() {
            timeout = null;
            if (!immediate) result = func.apply(ctx, args);
        };
        var callNow = immediate && !timeout;
        // Tant que la fonction est appelée, on reset le timeout.
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) result = func.apply(ctx, args);
        return result;
    };
}

// Component

class CarrouselComponent {
    /**
     * Construit toute l'interaction sur un carrousel à une seule slide affichée.
     * @param {HTMLElement} element Élément HTML concerné
     */
    constructor(element) {
        // Indique si le carrousel change automatiquement de slide
        this.autoSlide = !(this.isReducedMotion);
        // Identifiant de la routine d'animation du carrousel
        this.runInterval = null;
        // Index de la slide en cours d'affichage
        this._currentSlide = 0;
        // Élément HTML du carrousel
        this.element = element;
    }

    /**
     * Renvoie le nombre de slides à l'intérieur du carrousel.
     * @returns {number} Nombre de slides
     */
    get carrouselCount() {
        return Array.from(this.element.querySelectorAll(JS_CARROUSEL_SLIDE_SELECTOR)).length;
    }

    /**
     * Indique si l'utilisateur ne souhaite pas avoir d'animations
     * @returns {boolean} true s'il faut réduire les animations, false sinon
     */
    get isReducedMotion() {
        return window.matchMedia(`(prefers-reduced-motion: reduce)`) === true
            || window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === true;
    }

    /**
     * Change la slide à afficher et met le carrousel à jour
     * @param {Number} index Numéro de la slide à afficher
     */
    set currentSlide(index) {
        this._currentSlide = index;
        this.update();
        this.startAutoRun();
    }

    /**
     * Initialise le carrousel (événements)
     */
    init() {
        const transports = Array.from(this.element.querySelectorAll(JS_CARROUSEL_TRANSPORT_SELECTOR));
        for (const transport of transports) {
            transport.addEventListener(CLICK_EVENT, this.toggleAutoSlide.bind(this));
            transport.addEventListener(KEY_EVENT, this.keyPressHandler.bind(this));
        }
        const tabs = Array.from(this.element.querySelectorAll(JS_CARROUSEL_TAB_SELECTOR));
        for (const tab of tabs) {
            tab.addEventListener(CLICK_EVENT, this.tabClickHandler.bind(this));
            tab.addEventListener(KEY_EVENT, this.keyPressHandler.bind(this));
        }
        const slides = Array.from(this.element.querySelectorAll(JS_CARROUSEL_SLIDE_SELECTOR));
        for (const slide of slides) {
            slide.querySelector(LINK_SELECTOR).addEventListener(KEY_EVENT, this.keyPressHandler.bind(this));
        }
        this.element.querySelector(JS_CARROUSEL_SLIDES_SELECTOR).addEventListener(SCROLL_EVENT, this.scrollHandler.bind(this));
        this.element.querySelector(JS_CARROUSEL_SLIDES_SELECTOR).addEventListener(KEY_EVENT, e => {
            if ([
                ARROW_LEFT_EVENT_KEY,
                ARROW_RIGHT_EVENT_KEY,
                HOME_EVENT_KEY,
                END_EVENT_KEY
            ].includes(e.key)) {
                e.preventDefault();
                e.stopPropagation();
            }
        });
        const previousButtons = Array.from(this.element.querySelectorAll(JS_CARROUSEL_PREVIOUS_SELECTOR));
        for (const previous of previousButtons) {
            previous.addEventListener(CLICK_EVENT, this.previousSlide.bind(this));
            previous.addEventListener(KEY_EVENT, this.keyPressHandler.bind(this));
        }
        const nextButtons = Array.from(this.element.querySelectorAll(JS_CARROUSEL_NEXT_SELECTOR));
        for (const next of nextButtons) {
            next.addEventListener(CLICK_EVENT, this.nextSlide.bind(this));
            next.addEventListener(KEY_EVENT, this.keyPressHandler.bind(this));
        }
        this.update();
    }

    /**
     * Événement appelé quand on scrolle dans la zone d'affichage des slides.
     * @param {Event} e Événement JS
     */
    scrollHandler = debounce((e) => {
        const slides = e.target;
        this.currentSlide = Math.floor((slides.scrollLeft + slides.clientWidth / 2) / slides.scrollWidth * this.carrouselCount);
    }, DEBOUNCE_DELAY, false, this);

    /**
     * Événement appelé quand on clique sur l'un des boutons d'accès aux slides
     * @param {MouseEvent} e Événement JS
     */
    tabClickHandler(e) {
        e.preventDefault();
        this.currentSlide = Array.from(e.target.closest(JS_CARROUSEL_TABS_SELECTOR)?.children ?? [ ]).findIndex(i => i.contains(e.target));
    }

    /**
     * Événement appelé quand on appuie sur une touche du clavier
     * @param {KeyboardEvent} e Événement JS
     */
    keyPressHandler(e) {
        switch (e.key) {
            case ARROW_LEFT_EVENT_KEY: {
                e.preventDefault();
                e.stopPropagation();
                this.previousSlide();
                this.startAutoRun();
                break;
            }
            case ARROW_RIGHT_EVENT_KEY: {
                e.preventDefault();
                e.stopPropagation();
                this.nextSlide();
                this.startAutoRun();
                break;
            }
            case HOME_EVENT_KEY: {
                e.preventDefault();
                e.stopPropagation();
                this.currentSlide = 0;
                this.startAutoRun();
                break;
            }
            case END_EVENT_KEY: {
                e.preventDefault();
                e.stopPropagation();
                this.currentSlide = this.carrouselCount - 1;
                this.startAutoRun();
                break;
            }
        }
    }

    /**
     * Bascule l'activation du changement automatique de slides
     */
    toggleAutoSlide(e) {
        this.autoSlide = !(this.autoSlide);
        this.update();
    }

    /**
     * Affiche la slide précédente
     */
    previousSlide() {
        this.currentSlide = (this._currentSlide + this.carrouselCount - 1) % (this.carrouselCount);
    }

    /**
     * Affiche la slide suivante
     */
    nextSlide() {
        this.currentSlide = (this._currentSlide + 1) % (this.carrouselCount);
    }

    /**
     * Met à jour l'affichage du carrousel
     */
    update() {
        this.updateAutoSlideState()
        this.updateIndexState()
    }

    /**
     * Redémarre l'animation automatique
     */
    startAutoRun() {
        if (!(this.autoSlide)) return;
        this.stopAutoRun();
        this.runInterval = setInterval(this.nextSlide.bind(this), CARROUSEL_SLIDE_DELAY);
    }

    /**
     * Arrête l'animation automatique
     */
    stopAutoRun() {
        clearInterval(this.runInterval);
        this.runInterval = null;
    }

    /**
     * Met à jour l'état lié à l'animation automatique du carrousel
     */
    updateAutoSlideState() {
        this.element.classList.toggle(CARROUSEL_PLAYING_CLASSNAME, this.autoSlide);
        this.element.classList.toggle(CARROUSEL_PAUSED_CLASSNAME, !(this.autoSlide));
        if (this.autoSlide && !(this.runInterval)) {
            this.startAutoRun();
        } else if (!(this.autoSlide) && this.runInterval) {
            this.stopAutoRun();
        }
    }

    /**
     * Met à jour l'état lié à la slide en cours d'affichage
     */
    updateIndexState() {
        const tabs = Array.from(this.element.querySelectorAll(JS_CARROUSEL_TAB_SELECTOR));
        const slides = this.element.querySelector(JS_CARROUSEL_SLIDES_SELECTOR);
        const slideItems = Array.from(this.element.querySelectorAll(JS_CARROUSEL_SLIDE_SELECTOR));
        const tabHasFocus = this.element.querySelector(JS_CARROUSEL_TABS_SELECTOR)?.matches(FOCUS_WITHIN_SELECTOR);
        const slideHasFocus = slides.matches(FOCUS_WITHIN_SELECTOR);
        let currentTab = null;
        let currentSlide = null;
        for (let i = 0; i < this.carrouselCount; ++i) {
            const tab = tabs[i];
            const slide = slideItems[i];
            const isCurrent = i === this._currentSlide;
            if (isCurrent) {
                currentTab = tab;
                currentSlide = slide;
                tab?.removeAttribute(TABINDEX_ATTR);
                slide.querySelector(LINK_SELECTOR).setAttribute(TABINDEX_ATTR, ENABLED_TABINDEX_VALUE);
            } else {
                tab?.setAttribute(TABINDEX_ATTR, DISABLED_TABINDEX_VALUE);
                slide.querySelector(LINK_SELECTOR).setAttribute(TABINDEX_ATTR, DISABLED_TABINDEX_VALUE);
            }
            tab?.setAttribute(ARIA_SELECTED_ATTR, isCurrent ? TRUE_VALUE : FALSE_VALUE);
        }
        slides.scrollTo({
            behavior: this.isReducedMotion ? SCROLL_BEHAVIOR_AUTO_VALUE : SCROLL_BEHAVIOR_SMOOTH_VALUE,
            left: this._currentSlide * slides.clientWidth
        });
        if (tabHasFocus) currentTab?.focus();
        if (slideHasFocus) currentSlide?.querySelector(LINK_SELECTOR)?.focus();
    }
}

// Charge les carrousels présents sur la page au chargement
window.addEventListener(LOAD_EVENT, () => {
    const carrousels = Array.from(document.querySelectorAll(JS_CARROUSEL_SELECTOR));
    for (const carrousel of carrousels) {
        const component = new CarrouselComponent(carrousel);
        component.init();
    }
});